import CancellableHelper from "@/requests/CancellableHelper";
import { ServiceTypeAttributeTemplate } from "@/models/api/administration/servicesAndAttributes/ServiceTypeAttributeTemplate";
import { ServiceTypeAttributeTemplateEdit } from "@/models/api/administration/servicesAndAttributes/ServiceTypeAttributeTemplateEdit";
import { AttributeTemplateResponse } from "@/models/api/administration/servicesAndAttributes/AttributeTemplate";
import { ServiceTypeFull } from "@/models/api/serviceTypes/ServiceTypeFull";
import { ServiceTypeExportConfiguration } from "@/models/api/administration/servicesAndAttributes/ServiceTypeExportConfiguration";
import { NewExportConfiguration } from "@/models/api/administration/servicesAndAttributes/NewExportConfiguration";
import { SelectItem } from "@/models/api/SelectItem";
import {
  CopyServiceTypeSettings,
  ServiceTypeToCopyFrom,
} from "@/models/api/administration/servicesAndAttributes/ServiceTypeToCopyFrom";

//#region Routess
export const URL_GET_SERVICE_TYPE_ATTRIBUTE_TEMPLATE = "/ServiceType/${ServiceTypeId}/ServiceTypeAttributeTemplate";
export const URL_EDIT_SERVICE_TYPE_ATTRIBUTE_TEMPLATE =
  "/ServiceType/ServiceTypeAttributeTemplate/${ServiceTypeAttributeTemplateId}/Edit";
export const URL_POST_SLIDE_UP_SERVICE_TYPE_ATTRIBUTE_TEMPLATE =
  "/ServiceType/${ServiceTypeId}/ServiceTypeAttributeTemplate/${ServiceTypeAttributeTemplateId}/SlideUp";
export const URL_POST_SLIDE_DOWN_SERVICE_TYPE_ATTRIBUTE_TEMPLATE =
  "/ServiceType/${ServiceTypeId}/ServiceTypeAttributeTemplate/${ServiceTypeAttributeTemplateId}/SlideDown";
export const URL_POST_REMOVE_SERVICE_TYPE_ATTRIBUTE_TEMPLATE = "/ServiceType/ServiceTypeAttributeTemplate/Remove";
export const URL_POST_ADD_SERVICE_TYPE_ATTRIBUTE_TEMPLATE = "/ServiceType/ServiceTypeAttributeTemplate/Add";
export const URL_POST_ADD_SERVICE_TYPE = "/ServiceType/Add";
export const URL_POST_EDIT_SERVICE_TYPE = "/ServiceType/${ServiceTypeId}/Edit";
export const URL_POST_REMOVE_SERVICE_TYPE = "/ServiceType/${ServiceTypeId}/Remove";
export const URL_GET_EXPORT_CONFIGURATION_BY_SERVICE_TYPE = "/ServiceType/${ServiceTypeId}/ExportConfiguration";
export const URL_POST_ADD_EXPORT_CONFIGURATION_TO_SERVICE_TYPE =
  "/ServiceType/${ServiceTypeId}/ExportConfiguration/Add";
export const URL_POST_REMOVE_EXPORT_CONFIGURATION = "/ServiceType/ExportConfiguration/${ExportConfigurationId}/Remove";
export const URL_POST_EDIT_EXPORT_CONFIGURATION = "/ServiceType/ExportConfiguration/${ExportConfigurationId}/Edit";
export const URL_GET_ASSIGNED_COMMISSION_SERVICE_TYPE = "/ServiceType/${ServiceTypeId}/IsCommissionServiceType";
export const URL_GET_EXPORT_CONFIGURATION_TRANSACTION_TYPES = "/ServiceType/ExportConfiguration/TransactionTypes";
export const URL_GET_DISCOUNT_PUBLIC_PROCUREMENT_XML_DESCRIPTION =
  "/ServiceType/DiscountPublicProcurementXmlDescriptions";
export const URL_GET_FEE_PUBLIC_PROCUREMENT_XML_DESCRIPTION = "/ServiceType/FeePublicProcurementXmlDescriptions";
export const URL_GET_SERVICE_TYPES_TO_COPY_FROM = "/ServiceType/${ServiceTypeId}/ServiceTypesToCopyFrom";
export const URL_POST_COPY_SERVICE_TYPE_SETTINGS = "/ServiceType/${ServiceTypeId}/CopyServiceTypeSettings";
export const URL_GET_EXEMPTION_REASON_FOR_ZW_RATE = "/ServiceType/ExemptionReasonZWRate";
//#endregion

const _getServiceTypeAttributeTemplate = new CancellableHelper<Array<ServiceTypeAttributeTemplate>>(
  URL_GET_SERVICE_TYPE_ATTRIBUTE_TEMPLATE,
  ServiceTypeAttributeTemplate
);
export const getServiceTypeAttributeTemplate = async (
  serviceTypeId: number
): Promise<Array<ServiceTypeAttributeTemplate>> => {
  return await _getServiceTypeAttributeTemplate.get({
    ServiceTypeId: serviceTypeId,
  });
};

const _editServiceTypeAttributeTemplate = new CancellableHelper<null>(
  URL_EDIT_SERVICE_TYPE_ATTRIBUTE_TEMPLATE,
  null,
  true
);
export const editServiceTypeAttributeTemplate = async (
  serviceTypeAttributeTemplateId: number,
  serviceTypeAttributeTemplateEdit: ServiceTypeAttributeTemplateEdit
): Promise<null> => {
  return await _editServiceTypeAttributeTemplate.post(
    { ServiceTypeAttributeTemplateId: serviceTypeAttributeTemplateId },
    serviceTypeAttributeTemplateEdit
  );
};

const _slideUpServiceTypeAttributeTemplate = new CancellableHelper<AttributeTemplateResponse>(
  URL_POST_SLIDE_UP_SERVICE_TYPE_ATTRIBUTE_TEMPLATE,
  null,
  true
);
export const slideUpServiceTypeAttributeTemplate = async (
  serviceTypeId: number,
  serviceTypeAttributeTemplateId: number
): Promise<AttributeTemplateResponse> => {
  return await _slideUpServiceTypeAttributeTemplate.post(
    {
      ServiceTypeId: serviceTypeId,
      ServiceTypeAttributeTemplateId: serviceTypeAttributeTemplateId,
    },
    null
  );
};

const _slideDownServiceTypeAttributeTemplate = new CancellableHelper<AttributeTemplateResponse>(
  URL_POST_SLIDE_DOWN_SERVICE_TYPE_ATTRIBUTE_TEMPLATE,
  null,
  true
);
export const slideDownServiceTypeAttributeTemplate = async (
  serviceTypeId: number,
  serviceTypeAttributeTemplateId: number
): Promise<AttributeTemplateResponse> => {
  return await _slideDownServiceTypeAttributeTemplate.post(
    {
      ServiceTypeId: serviceTypeId,
      ServiceTypeAttributeTemplateId: serviceTypeAttributeTemplateId,
    },
    null
  );
};

const _removeServiceTypeAttributeTemplate = new CancellableHelper<AttributeTemplateResponse>(
  URL_POST_REMOVE_SERVICE_TYPE_ATTRIBUTE_TEMPLATE,
  null,
  true
);
export const removeServiceTypeAttributeTemplate = async (
  serviceTypeIds: Array<number>,
  attributeTemplateIds: Array<number>
): Promise<AttributeTemplateResponse> => {
  return await _removeServiceTypeAttributeTemplate.post(
    {},
    {
      ServiceTypeIds: serviceTypeIds,
      AttributeTemplateIds: attributeTemplateIds,
    }
  );
};

const _addServiceTypeAttributeTemplate = new CancellableHelper<AttributeTemplateResponse>(
  URL_POST_ADD_SERVICE_TYPE_ATTRIBUTE_TEMPLATE,
  null,
  true
);
export const addServiceTypeAttributeTemplate = async (
  serviceTypeIds: Array<number>,
  attributeTemplateIds: Array<number>
): Promise<AttributeTemplateResponse> => {
  return await _addServiceTypeAttributeTemplate.post(
    {},
    {
      ServiceTypeIds: serviceTypeIds,
      AttributeTemplateIds: attributeTemplateIds,
    }
  );
};

const _addServiceType = new CancellableHelper<AttributeTemplateResponse>(URL_POST_ADD_SERVICE_TYPE, null, true);
export const addServiceType = async (serviceTypeFull: ServiceTypeFull): Promise<AttributeTemplateResponse> => {
  return await _addServiceType.post({}, serviceTypeFull);
};

const _editServiceType = new CancellableHelper<AttributeTemplateResponse>(URL_POST_EDIT_SERVICE_TYPE, null, true);
export const editServiceType = async (
  serviceTypeFull: ServiceTypeFull,
  serviceTypeId: number
): Promise<AttributeTemplateResponse> => {
  return await _editServiceType.post({ ServiceTypeId: serviceTypeId }, serviceTypeFull);
};

const _removeServiceType = new CancellableHelper<ServiceTypeFull>(URL_POST_REMOVE_SERVICE_TYPE, null, true);
export const removeServiceType = async (serviceTypeId: number): Promise<ServiceTypeFull> => {
  return await _removeServiceType.post({ ServiceTypeId: serviceTypeId }, null);
};

const _getExportConfigurationByServiceType = new CancellableHelper<ServiceTypeExportConfiguration>(
  URL_GET_EXPORT_CONFIGURATION_BY_SERVICE_TYPE,
  ServiceTypeExportConfiguration
);
export const getExportConfigurationByServiceType = async (
  serviceTypeId: number
): Promise<ServiceTypeExportConfiguration> => {
  return await _getExportConfigurationByServiceType.get({
    ServiceTypeId: serviceTypeId,
  });
};

const _addExportConfigurationToServiceType = new CancellableHelper<ServiceTypeFull>(
  URL_POST_ADD_EXPORT_CONFIGURATION_TO_SERVICE_TYPE,
  null,
  true
);
export const addExportConfigurationToServiceType = async (
  newExportConfigurations: Array<NewExportConfiguration>,
  serviceTypeId: number
): Promise<ServiceTypeFull> => {
  return await _addExportConfigurationToServiceType.post({ ServiceTypeId: serviceTypeId }, newExportConfigurations);
};

const _removeExportConfiguration = new CancellableHelper<ServiceTypeFull>(
  URL_POST_REMOVE_EXPORT_CONFIGURATION,
  null,
  true
);
export const removeExportConfiguration = async (exportConfigurationId: number): Promise<ServiceTypeFull> => {
  return await _removeExportConfiguration.post({ ExportConfigurationId: exportConfigurationId }, null);
};

const _editExportConfiguration = new CancellableHelper<ServiceTypeFull>(URL_POST_EDIT_EXPORT_CONFIGURATION, null, true);
export const editExportConfiguration = async (
  newExportConfiguration: NewExportConfiguration,
  exportConfigurationId: number
): Promise<ServiceTypeFull> => {
  return await _editExportConfiguration.post({ ExportConfigurationId: exportConfigurationId }, newExportConfiguration);
};

const _getAssignedCommissionServiceType = new CancellableHelper<Array<string>>(
  URL_GET_ASSIGNED_COMMISSION_SERVICE_TYPE,
  "string"
);

export const getAssignedCommissionServiceType = async (serviceTypeId: number): Promise<Array<string>> => {
  return await _getAssignedCommissionServiceType.get({
    ServiceTypeId: serviceTypeId,
  });
};

const _getExportConfigurationTransactionTypes = new CancellableHelper<Array<SelectItem>>(
  URL_GET_EXPORT_CONFIGURATION_TRANSACTION_TYPES,
  SelectItem
);
export const getExportConfigurationTransactionTypes = async (): Promise<Array<SelectItem>> => {
  return await _getExportConfigurationTransactionTypes.get();
};

const _getDiscountPublicProcurementXmlDescriptions = new CancellableHelper<Array<SelectItem>>(
  URL_GET_DISCOUNT_PUBLIC_PROCUREMENT_XML_DESCRIPTION,
  SelectItem
);
export const getDiscountPublicProcurementXmlDescriptions = async (): Promise<Array<SelectItem>> => {
  return await _getDiscountPublicProcurementXmlDescriptions.get();
};

const _getExemptionReasonForZWRate = new CancellableHelper<Array<SelectItem>>(
  URL_GET_EXEMPTION_REASON_FOR_ZW_RATE,
  SelectItem
);
export const getExemptionReasonForZWRate = async (): Promise<Array<SelectItem>> => {
  return await _getExemptionReasonForZWRate.get();
};

const _getFeePublicProcurementXmlDescriptions = new CancellableHelper<Array<SelectItem>>(
  URL_GET_FEE_PUBLIC_PROCUREMENT_XML_DESCRIPTION,
  SelectItem
);
export const getFeePublicProcurementXmlDescriptions = async (): Promise<Array<SelectItem>> => {
  return await _getFeePublicProcurementXmlDescriptions.get();
};

const _getServiceTypesToCopyFrom = new CancellableHelper<Array<ServiceTypeToCopyFrom>>(
  URL_GET_SERVICE_TYPES_TO_COPY_FROM,
  ServiceTypeToCopyFrom
);
export const getServiceTypesToCopyFrom = async (serviceTypeId: number): Promise<Array<ServiceTypeToCopyFrom>> => {
  return await _getServiceTypesToCopyFrom.get({
    ServiceTypeId: serviceTypeId,
  });
};

const _postCopyServiceTypeSettings = new CancellableHelper<null>(URL_POST_COPY_SERVICE_TYPE_SETTINGS, null, true);
export const postCopyServiceTypeSettings = async (
  serviceTypeId: number,
  model: CopyServiceTypeSettings
): Promise<null> => {
  return await _postCopyServiceTypeSettings.post({ ServiceTypeId: serviceTypeId }, model);
};
